import { useCallback, useEffect, useMemo, useState } from "react";
import { ShieldCheck, Plus, Beaker } from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import CreateWebhookModal from "./CreateWebhookModal";
import WebhookHealthBadge from "./WebhookHealthBadge";
import RotateSecretModal from "./RotateSecretModal";

const toData = response =>
  response?.data?.data ?? response?.data?.payload ?? response?.data ?? null;

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const parseEvents = value => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeEndpoint = row => ({
  id: row?.id,
  url: row?.url || "",
  events: parseEvents(row?.events ?? row?.subscribedEventsJson),
  isActive: row?.isActive !== false,
  createdAtUtc: row?.createdAtUtc,
  updatedAtUtc: row?.updatedAtUtc,
  healthStatus: row?.healthStatus || "NoData",
  lastSuccessAtUtc: row?.lastSuccessAtUtc || null,
  lastFailureAtUtc: row?.lastFailureAtUtc || null,
  pendingRetryCount: Number(row?.pendingRetryCount || 0),
  recentFailureCount: Number(row?.recentFailureCount || 0),
  lastFailureStatusCode:
    row?.lastFailureStatusCode === null || row?.lastFailureStatusCode === undefined
      ? null
      : row.lastFailureStatusCode,
  lastFailureSnippet: row?.lastFailureSnippet || "",
});

const formatDate = value => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const needsAttention = status => status === "Failing" || status === "Degraded";

const attentionRowStyles = status => {
  if (status === "Failing") {
    return "bg-rose-50/60";
  }

  if (status === "Degraded") {
    return "bg-amber-50/60";
  }

  return "";
};

export default function WebhooksSection({
  onEndpointsChange,
  refreshTick,
  onRefreshComplete,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAttentionOnly, setShowAttentionOnly] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rotatedSecret, setRotatedSecret] = useState(null);
  const [sendingTestId, setSendingTestId] = useState("");

  const emitChange = useCallback(
    next => {
      setRows(next);
      if (typeof onEndpointsChange === "function") {
        onEndpointsChange(next);
      }
    },
    [onEndpointsChange]
  );

  const load = useCallback(async (tick = refreshTick) => {
    setLoading(true);
    try {
      const res = await axiosClient.get("/webhooks/endpoints", {
        __silentGenericToast: true,
      });
      const data = toData(res);
      const list = Array.isArray(data)
        ? data
        : data?.items || data?.endpoints || [];
      emitChange((Array.isArray(list) ? list : []).map(normalizeEndpoint));
    } catch (error) {
      if (error?.response?.status !== 429) {
        toast.error(getErrorMessage(error, "Failed to load webhook endpoints"), {
          toastId: "integrations-webhooks-load",
        });
      }
    } finally {
      setLoading(false);
      if (typeof onRefreshComplete === "function") {
        onRefreshComplete(tick);
      }
    }
  }, [emitChange, onRefreshComplete, refreshTick]);

  useEffect(() => {
    load(refreshTick);
  }, [load, refreshTick]);

  const createOrUpdate = async payload => {
    setBusy(true);
    try {
      if (editing?.id) {
        await axiosClient.put(`/webhooks/endpoints/${editing.id}`, payload, {
          __silentGenericToast: true,
        });
        toast.success("Webhook endpoint updated");
      } else {
        await axiosClient.post("/webhooks/endpoints", payload, {
          __silentGenericToast: true,
        });
        toast.success("Webhook endpoint created");
      }
      setShowCreate(false);
      setEditing(null);
      await load();
    } catch (error) {
      if (error?.response?.status !== 429) {
        toast.error(getErrorMessage(error, "Failed to save endpoint"));
      }
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async row => {
    try {
      await axiosClient.put(
        `/webhooks/endpoints/${row.id}`,
        {
          url: row.url,
          events: row.events,
          isActive: !row.isActive,
        },
        { __silentGenericToast: true }
      );
      toast.success("Endpoint updated");
      await load();
    } catch (error) {
      if (error?.response?.status !== 429) {
        toast.error(getErrorMessage(error, "Failed to update endpoint"));
      }
    }
  };

  const rotateSecret = async row => {
    try {
      const res = await axiosClient.post(
        `/webhooks/endpoints/${row.id}/rotate-secret`,
        null,
        { __silentGenericToast: true }
      );
      setRotatedSecret(toData(res));
      toast.success("Secret rotated");
    } catch (error) {
      if (error?.response?.status !== 429) {
        toast.error(getErrorMessage(error, "Failed to rotate secret"));
      }
    }
  };

  const sendTestCallback = async row => {
    setSendingTestId(row.id);
    try {
      await axiosClient.post(`/webhooks/endpoints/${row.id}/test-callback`, null, {
        __silentGenericToast: true,
      });
      toast.success("Test callback queued");
    } catch (error) {
      if (error?.response?.status !== 429) {
        toast.error(getErrorMessage(error, "Failed to send test callback"));
      }
    } finally {
      setSendingTestId("");
    }
  };

  const visibleRows = useMemo(() => {
    if (!showAttentionOnly) {
      return rows;
    }

    return rows.filter(row => needsAttention(row.healthStatus));
  }, [rows, showAttentionOnly]);

  const hasRows = useMemo(() => rows.length > 0, [rows]);
  const hasVisibleRows = useMemo(() => visibleRows.length > 0, [visibleRows]);

  return (
    <section className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-800">Webhooks</h2>
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setShowCreate(true);
          }}
          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-2 text-sm text-white"
        >
          <Plus size={14} />
          Add Endpoint
        </button>
      </div>

      <label className="mb-4 inline-flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={showAttentionOnly}
          onChange={event => setShowAttentionOnly(event.target.checked)}
        />
        Show only endpoints needing attention
      </label>

      <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-4">
        <p className="text-sm font-medium text-slate-800">Callback security and testing</p>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Outgoing callbacks are signed with{" "}
          <code>X-XploreByte-Signature</code> using the format{" "}
          <code>t=&lt;unix_ts&gt;,v1=&lt;hex_hmac_sha256&gt;</code> over{" "}
          <code>&lt;timestamp&gt;.&lt;raw_body&gt;</code>. Rotate the webhook
          secret when needed. Use <code>Send Test Callback</code> to deliver a
          signed <code>webhook.test</code> event directly to one active endpoint.
        </p>
        <p className="mt-2 text-xs leading-5 text-slate-500">
          Test callbacks verify reachability and signature handling, do not affect
          endpoint health status, and require the endpoint to be active.
        </p>
        <p className="mt-2 text-xs leading-5 text-slate-500">
          <code>provider.meta.raw</code> is for systems that already parse
          WhatsApp Cloud API webhooks. It forwards only actual, signature-validated
          Meta payloads without an XploreByte envelope. Test callbacks always remain
          signed XploreByte <code>webhook.test</code> events and do not simulate raw
          Meta traffic.
        </p>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : !hasRows ? (
        <p className="text-sm text-slate-500">
          No webhook endpoints found. Add your first endpoint to receive signed
          delivery events and test callbacks.
        </p>
      ) : !hasVisibleRows ? (
        <p className="text-sm text-slate-500">
          No endpoints currently need attention.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-slate-600">
                <th className="py-2 pr-3">URL</th>
                <th className="py-2 pr-3">Events</th>
                <th className="py-2 pr-3">Health</th>
                <th className="py-2 pr-3">Active</th>
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3">Updated</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map(row => (
                <tr
                  key={row.id}
                  className={`border-b border-slate-100 align-top ${attentionRowStyles(row.healthStatus)}`}
                >
                  <td className="py-2 pr-3 max-w-[300px] break-all">{row.url}</td>
                  <td className="py-2 pr-3">{row.events.join(", ") || "-"}</td>
                  <td className="py-2 pr-3">
                    <div className="space-y-1">
                      <WebhookHealthBadge status={row.healthStatus} />
                      {needsAttention(row.healthStatus) ? (
                        <p
                          className={`text-xs font-medium ${
                            row.healthStatus === "Failing" ? "text-rose-700" : "text-amber-700"
                          }`}
                        >
                          Needs attention
                        </p>
                      ) : null}
                      {row.pendingRetryCount > 0 ? (
                        <p className="text-xs text-amber-700">
                          {row.pendingRetryCount} pending retr{row.pendingRetryCount === 1 ? "y" : "ies"}
                        </p>
                      ) : row.recentFailureCount > 0 ? (
                        <p className="text-xs text-rose-700">
                          {row.recentFailureCount} unresolved failur{row.recentFailureCount === 1 ? "e" : "es"}
                        </p>
                      ) : (
                        <p className="text-xs text-slate-500">No active issue</p>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!!row.isActive}
                        onChange={() => toggleActive(row)}
                      />
                      {row.isActive ? "Yes" : "No"}
                    </label>
                  </td>
                  <td className="py-2 pr-3">{formatDate(row.createdAtUtc)}</td>
                  <td className="py-2 pr-3">{formatDate(row.updatedAtUtc)}</td>
                  <td className="py-2">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(row);
                          setShowCreate(true);
                        }}
                        className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => sendTestCallback(row)}
                        disabled={!row.isActive || sendingTestId === row.id}
                        className="inline-flex items-center gap-1 rounded border border-sky-300 px-2 py-1 text-xs text-sky-800 hover:bg-sky-50 disabled:opacity-60"
                      >
                        <Beaker size={12} />
                        {sendingTestId === row.id ? "Sending..." : "Send Test Callback"}
                      </button>
                      <button
                        type="button"
                        onClick={() => rotateSecret(row)}
                        className="inline-flex items-center gap-1 rounded border border-amber-300 px-2 py-1 text-xs text-amber-800 hover:bg-amber-50"
                      >
                        <ShieldCheck size={12} />
                        Rotate Secret
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateWebhookModal
        isOpen={showCreate}
        onClose={() => {
          setShowCreate(false);
          setEditing(null);
        }}
        onSubmit={createOrUpdate}
        busy={busy}
        initialValue={editing}
      />

      <RotateSecretModal
        isOpen={!!rotatedSecret}
        onClose={() => setRotatedSecret(null)}
        data={rotatedSecret}
      />
    </section>
  );
}
